import React, { useEffect, useState } from "react";
import {
  Form,
  Input,
  Button,
  Row,
  Col,
  Card,
  Avatar,
  Typography,
  Divider,
  Upload,
  message,
  Space,
  Tabs,
} from "antd";
import {
  EditFilled,
  UploadOutlined,
  SaveOutlined,
  CloseOutlined,
  UserOutlined,
  CreditCardOutlined,
} from "@ant-design/icons";
import { useFarmProfile } from "../../../contexts/FarmProfileContext";
import api from "../../../api/client";
import { BillingPage } from "../../../components/BillingPage";

const { Title, Text } = Typography;

const FarmProfile = () => {
  const [profile, setProfile] = useState(null);
  const [form] = Form.useForm();
  const [logoFile, setLogoFile] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const { updateProfile } = useFarmProfile();

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const res = await api.get(`/api/farm-profile`);
      if (res.data?.id) {
        setProfile(res.data);
        form.setFieldsValue(res.data);
        setIsEditing(false);
        updateProfile(res.data);
      } else {
        setIsEditing(true);
      }
    } catch (error) {
      message.error("Failed to load farm profile");
    }
  };

  const handleLogoChange = ({ file }) => {
    if (file.status === "done" || file.originFileObj) {
      setLogoFile(file.originFileObj || file);
    }
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    const data = new FormData();
    for (let key in values) {
      if (values[key]) data.append(key, values[key]);
    }
    if (logoFile) data.append("logo", logoFile);

    try {
      await api.post(`/api/farm-profile`, data);
      await fetchProfile();
      message.success("Profile saved successfully!");
      setIsEditing(false);
    } catch (err) {
      message.error("Failed to save profile.");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (profile) {
      form.setFieldsValue(profile);
      setLogoFile(null);
    }
    setIsEditing(false);
  };

  // ---- Profile display block (view mode) ----
  const renderProfileView = () => (
    <Card
      title="Farm Profile"
      extra={
        <Button icon={<EditFilled />} onClick={() => setIsEditing(true)}>
          Edit
        </Button>
      }
    >
      <Card
        bordered
        style={{ maxWidth: 800, margin: "0 auto", borderRadius: 12 }}
        bodyStyle={{ padding: 24 }}
      >
        <Row gutter={24} align="middle">
          <Col xs={24} md={6} style={{ textAlign: "center" }}>
            {profile.logo_url && (
              <Avatar
                src={
                  profile.logo_url.startsWith("data:")
                    ? profile.logo_url
                    : `${api.defaults.baseURL}${profile.logo_url}`
                }
                shape="square"
                size={120}
                style={{
                  border: "1px solid #f0f0f0",
                  objectFit: "contain",
                  background: "#fff",
                }}
              />
            )}
          </Col>
          <Col xs={24} md={18}>
            {profile.name && profile.name !== "null" && (
              <Title level={3} style={{ marginBottom: 8 }}>
                {profile.name}
              </Title>
            )}
            {profile.email && profile.email !== "null" && (
              <>
                <Text strong>Email: </Text>
                <Text>{profile.email}</Text>
                <Divider style={{ margin: "12px 0" }} />
              </>
            )}
            <Row gutter={[16, 8]}>
              {profile.phone1 && profile.phone1 !== "null" && (
                <Col span={12}>
                  <Text strong>Phone 1: </Text>
                  <Text>{profile.phone1}</Text>
                </Col>
              )}
              {profile.phone2 && profile.phone2 !== "null" && (
                <Col span={12}>
                  <Text strong>Phone 2: </Text>
                  <Text>{profile.phone2}</Text>
                </Col>
              )}
              {profile.address1 && profile.address1 !== "null" && (
                <Col span={12}>
                  <Text strong>Address 1: </Text>
                  <Text>{profile.address1}</Text>
                </Col>
              )}
              {profile.address2 && profile.address2 !== "null" && (
                <Col span={12}>
                  <Text strong>Address 2: </Text>
                  <Text>{profile.address2}</Text>
                </Col>
              )}
              {profile.town && profile.town !== "null" && (
                <Col span={12}>
                  <Text strong>Town: </Text>
                  <Text>{profile.town}</Text>
                </Col>
              )}
              {profile.location && profile.location !== "null" && (
                <Col span={12}>
                  <Text strong>Location: </Text>
                  <Text>{profile.location}</Text>
                </Col>
              )}
            </Row>
          </Col>
        </Row>
      </Card>
    </Card>
  );

  // ---- Profile edit/create form block ----
  const renderProfileForm = () => (
    <Card title={profile ? "Edit Farm Profile" : "Create Farm Profile"}>
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={profile || {}}
      >
        <Form.Item
          name="name"
          label="Farm Name"
          rules={[{ required: true, message: "Please enter farm name" }]}
        >
          <Input placeholder="Enter farm name" />
        </Form.Item>
        <Form.Item
          name="email"
          label="Email"
          rules={[{ required: true, type: "email", message: "Valid email required" }]}
        >
          <Input placeholder="example@example.com" />
        </Form.Item>
        <Form.Item
          name="phone1"
          label="Phone 1"
          rules={[{ required: true, message: "Please enter phone number" }]}
        >
          <Input placeholder="254700000000" />
        </Form.Item>
        <Form.Item name="phone2" label="Phone 2">
          <Input placeholder="254700000000" />
        </Form.Item>
        <Form.Item
          name="address1"
          label="Address line 1"
          rules={[{ required: true, message: "Please enter address line 1" }]}
        >
          <Input placeholder="House, floor, street, etc." />
        </Form.Item>
        <Form.Item
          name="address2"
          label="Address line 2"
          rules={[{ required: true, message: "Please enter address line 2" }]}
        >
          <Input placeholder="P. O. Box 0000 - 00100 Nairobi, Kenya." />
        </Form.Item>
        <Form.Item name="town" label="Town">
          <Input placeholder="Town / City" />
        </Form.Item>
        <Form.Item name="location" label="Location">
          <Input placeholder="Location / Area" />
        </Form.Item>

        <Form.Item label="Logo">
          <Upload
            beforeUpload={() => false}
            onChange={handleLogoChange}
            accept="image/png,image/jpeg"
            showUploadList={false}
          >
            <Button icon={<UploadOutlined />}>Select Logo</Button>
          </Upload>
          {logoFile && (
            <div style={{ marginTop: 8 }}>Selected: {logoFile.name}</div>
          )}
          {profile?.logo_url && !logoFile && (
            <div style={{ marginTop: 8 }}>
              <img
                src={
                  profile.logo_url.startsWith("data:")
                    ? profile.logo_url
                    : `${api.defaults.baseURL}${profile.logo_url}`
                }
                alt="Current Logo"
                style={{ maxWidth: 200, maxHeight: 100, marginTop: 8 }}
              />
            </div>
          )}
        </Form.Item>

        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={loading}>
              Save
            </Button>
            {profile && (
              <Button onClick={handleCancel} icon={<CloseOutlined />}>
                Cancel
              </Button>
            )}
          </Space>
        </Form.Item>
      </Form>
    </Card>
  );

  const profileTabContent = !isEditing && profile ? renderProfileView() : renderProfileForm();

  return (
    <Row>
      <Col span={24}>
        <div style={{ marginBottom: 16 }}>
          <Title level={3} style={{ marginBottom: 0 }}>
            Farm Settings
          </Title>
          <Text type="secondary">
            Manage your farm's profile information and payment integrations.
          </Text>
        </div>

        <Tabs
          defaultActiveKey="profile"
          type="card"
          items={[
            {
              key: "profile",
              label: (
                <span>
                  <UserOutlined /> Profile
                </span>
              ),
              children: profileTabContent,
            },
            {
              key: "billing",
              label: (
                <span>
                  <CreditCardOutlined /> Billing & Payments
                </span>
              ),
              children: profile?.id ? (
                <BillingPage farmId={profile.id} />
              ) : (
                <Card>
                  <Text type="secondary">
                    Complete your farm profile first to configure billing and payment settings.
                  </Text>
                </Card>
              ),
              disabled: !profile?.id,
            },
          ]}
        />
      </Col>
    </Row>
  );
};

export default FarmProfile;